import type React from 'react';

import { Card, CardBody, Link, Text } from '@change/ds-core-ui';
import type { InsightsIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

type Props = Readonly<{
	cardKey: string;
	href: string;
	onClick: () => void;
	/**
	 * True for tiled/cloned copies rendered only to fill out the marquee ring - keeps every
	 * petition announced, and tabbed to, exactly once via aria-hidden + tabIndex. Deliberately
	 * NOT `inert`: the loop slides these copies into view, so they must stay clickable.
	 */
	duplicate?: boolean;
	/** Leads the header row, beside the label. Omitted along with the label where a card has none. */
	icon?: typeof InsightsIcon;
	/** Header row, left side. */
	label?: string;
	/** Header row, right side - the relative time. */
	trailingLabel?: string;
	className?: string;
	/** The card's own content, below the header row. */
	children: React.ReactNode;
}>;

/**
 * The frame every marquee card sits in: a header row of icon + label with a relative time
 * opposite, then whatever the card puts below it.
 */
export const CardWrapper: React.FC<Props> = ({
	cardKey,
	href,
	onClick,
	duplicate = false,
	icon: Icon,
	label,
	trailingLabel,
	className: cardClassName,
	children,
}) => (
	// Spacing is a trailing margin (not a track gap) so the -50% loop point lands exactly
	// between two cards, and the fixed width is what the focus snap measures.
	<li className="me-4 w-80 shrink-0" aria-hidden={duplicate || undefined}>
		{/* The whole card is the link: a real <a> gives keyboard activation, open-in-new-tab and
		    link semantics for free, and routes client-side via the DS RouterProvider. */}
		<Card
			as={Link}
			href={href}
			onClick={onClick}
			underline={false}
			tabIndex={duplicate ? -1 : undefined}
			className={cn(
				'hover:elevation-low bg-background-base !text-typography-strong hover:!text-typography-strong visited:!text-typography-strong h-full cursor-pointer items-start rounded-xl [--card-padding:12px] focus-visible:rounded-xl',
				cardClassName,
			)}
			data-testid="petition-marquee-card"
			data-qa={`petition-marquee-card-${cardKey}`}
		>
			<CardBody className="flex h-full grow flex-col text-left">
				{(label || trailingLabel) && (
					<div className="mb-3 flex items-center justify-between gap-2">
						<span className="flex min-w-0 items-center gap-1">
							{Icon && <Icon className="text-icon-brand shrink-0" size={14} />}
							{label && (
								<Text as="span" variant="body" size="x-small" fontWeight="bold" truncate>
									{label}
								</Text>
							)}
						</span>
						{trailingLabel && (
							<Text as="span" variant="body" size="x-small" className="text-typography-weak shrink-0">
								{trailingLabel}
							</Text>
						)}
					</div>
				)}
				{children}
			</CardBody>
		</Card>
	</li>
);
